'use client';

import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react';
import { cn } from '../utils.ts';

export type ToastTone = 'info' | 'success' | 'warn' | 'danger';

interface ToastEntry {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  tone: ToastTone;
  durationMs: number;
}

interface PushArgs {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  durationMs?: number;
}

interface ToastContextValue {
  push: (args: PushArgs) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toneStyles: Record<ToastTone, string> = {
  info: 'border-electric/30 bg-surface',
  success: 'border-success/40 bg-surface',
  warn: 'border-warn/40 bg-surface',
  danger: 'border-danger/40 bg-surface',
};

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  danger: XCircle,
} satisfies Record<ToastTone, typeof Info>;

const iconColours: Record<ToastTone, string> = {
  info: 'text-electric',
  success: 'text-success',
  warn: 'text-warn',
  danger: 'text-danger',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([]);

  const push = useCallback((args: PushArgs) => {
    const id = `toast_${Math.random().toString(36).slice(2, 10)}`;
    const tone = args.tone ?? 'info';
    setEntries((prev) => [
      ...prev,
      {
        id,
        title: args.title,
        description: args.description,
        tone,
        durationMs: args.durationMs ?? (tone === 'danger' ? 8000 : 4200),
      },
    ]);
    return id;
  }, []);

  const dismiss = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right" duration={6000}>
        {children}
        {entries.map((entry) => {
          const Icon = ICONS[entry.tone];
          return (
            <ToastPrimitive.Root
              key={entry.id}
              duration={entry.durationMs}
              onOpenChange={(open) => {
                if (!open) dismiss(entry.id);
              }}
              className={cn(
                'group pointer-events-auto relative flex items-start gap-3 rounded-card border bg-surface p-4 shadow-hover',
                'data-[state=open]:animate-toast-in data-[state=closed]:animate-toast-out motion-reduce:animate-none',
                toneStyles[entry.tone],
              )}
            >
              <Icon className={cn('h-5 w-5 flex-none mt-0.5', iconColours[entry.tone])} />
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-body-md font-semibold text-midnight">
                  {entry.title}
                </ToastPrimitive.Title>
                {entry.description ? (
                  <ToastPrimitive.Description className="mt-0.5 text-[13px] text-cool-gray leading-snug">
                    {entry.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              <ToastPrimitive.Close
                aria-label="Dismiss notification"
                className="flex-none rounded-sm p-1 text-cool-gray hover:bg-light-silver focus-visible:outline-none focus-visible:shadow-focus-ring"
              >
                <X className="h-3.5 w-3.5" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport
          className={cn(
            'fixed bottom-4 right-4 z-[100] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none',
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

/** Helper: render a static success/danger toast inline (rarely used; prefer the hook). */
export function InlineToast({
  title,
  description,
  tone = 'info',
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  className?: string;
}) {
  const Icon = ICONS[tone];
  const _id = useId();
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-card border bg-surface p-4 shadow-soft',
        toneStyles[tone],
        className,
      )}
    >
      <Icon className={cn('h-5 w-5 flex-none mt-0.5', iconColours[tone])} />
      <div className="min-w-0 flex-1">
        <p className="text-body-md font-semibold text-midnight">{title}</p>
        {description ? <p className="mt-0.5 text-[13px] text-cool-gray">{description}</p> : null}
      </div>
    </div>
  );
}
